import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { FormField } from 'ui/formField';
import { TextArea } from 'ui/inputs/textArea';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalFooter, ModalHeader, ModalBody } from 'ui/modal';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { JourneyDetailText } from 'modules/journey/components/SettingsTakeover/AboutJourney';
import { StyledInput } from './WorkflowDetailsModal.styled';
import { closeModal, getWorkflow } from '../../../ducks';
import { WORKFLOW_DETAILS_MODAL } from '../../../constants';
import { useUpdateWorkflowDetails } from '../../../hooks/useUpdateWorkflowDetails';
import {
  modalText,
  WORKFLOW_DETAILS_DESCRIPTION,
  WORKFLOW_DETAILS_NAME,
  WORKFLOW_DETAILS_SUBMIT,
} from './WorkflowDetailsModal.constants';
import { JourneyDetails } from './JourneyDetails';

type Props = {
  /** Whether modal is open and visible */
  isOpen: boolean;
};

export const WorkflowDetailsModal = ({ isOpen }: Props) => {
  const workflow = useSelector(getWorkflow);

  const { description, name, id: workflowId } = workflow;
  const [workflowNameInput, setWorkflowNameInput] = useState(name || '');
  const [workflowDescriptionInput, setWorkflowDescriptionInput] = useState(description || '');
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );

  /* Change Handlers */
  const dispatch = useDispatch();

  const updateWorkflowDetails = useUpdateWorkflowDetails();

  const closeWorkflowDetailsModal = () => {
    dispatch(closeModal(WORKFLOW_DETAILS_MODAL));
  };

  const saveWorkflowDetails = () => {
    const workflowDetails = {
      workflowId,
      workflowType: workflow.workflowType,
      name: workflowNameInput,
      description: workflowDescriptionInput,
    };
    updateWorkflowDetails({ workflowDetails, onSuccess: closeWorkflowDetailsModal });
  };

  return (
    <Modal
      id={WORKFLOW_DETAILS_MODAL}
      isModalOpen={isOpen}
      onClose={closeWorkflowDetailsModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{modalText.modalTitle}</ModalHeader>
      <ModalBody>
        <FormField inputId="workflow-name-input" labelText={modalText.workflowNameLabel}>
          <StyledInput
            id="workflow-name-input"
            data-test={WORKFLOW_DETAILS_NAME}
            onChange={setWorkflowNameInput}
            value={workflowNameInput}
            isDisabled={!canDraftMessaging}
          />
          <JourneyDetailText />
        </FormField>
        <FormField inputId="workflow-description-input" labelText={modalText.descriptionLabel}>
          <TextArea
            id="workflow-description-textarea"
            data-test={WORKFLOW_DETAILS_DESCRIPTION}
            maxLength={140}
            onChange={setWorkflowDescriptionInput}
            placeholder={description ? '' : modalText.descriptionInputPlaceholder}
            value={workflowDescriptionInput}
            isDisabled={!canDraftMessaging}
            isCharacterCountShown
          />
        </FormField>
        <JourneyDetails />
      </ModalBody>
      {/* Display modal actions only if user is allowed to edit the fields */}
      {canDraftMessaging ? (
        <ModalFooter>
          <Button
            isDisabled={!canDraftMessaging}
            data-test={WORKFLOW_DETAILS_SUBMIT}
            autoFocus
            onClick={saveWorkflowDetails}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
          >
            {modalText.updateButtonLabel}
          </Button>
        </ModalFooter>
      ) : (
        <></>
      )}
    </Modal>
  );
};
